import { readonly, ref, type Ref } from 'vue';
import type { jsPDF } from 'jspdf';
import { useBranchLogo } from '@/entities/Branch';
import { useCustomToast } from '@/shared/lib';
import { useDashboardStore } from './dashboard.store';
import {
  buildExportFileName,
  buildGeneratedAtLabel,
  buildPdfLayout,
  type PdfPageConfig,
  type WidgetRect,
} from './export-layout';
import { formatPeriodLabel } from './period';

const PAGE_CONFIG: PdfPageConfig = {
  pageWidth: 210,
  pageHeight: 297,
  margin: 10,
  headerHeight: 16,
};

const CAPTURE_OPTIONS = { scale: 2, dpr: 1, embedFonts: true };

const CAPTURE_SETTLE_MS = 700;

export const EXPORT_STAGE_CLASS = 'dashboard-export-stage';

// The PDF is paper: the capture must read the light palette even when the app is dark.
const LIGHT_SURFACE_CLASS = 'light-surface';

const HEADER_TEXT_COLOR = '#2f2f2f';
const HEADER_SUBTEXT_COLOR = '#66707a';
const HEADER_LINE_COLOR = '#e4e7ec';

type Snapdom = typeof import('@zumer/snapdom')['snapdom'];

interface WidgetCapture {
  dataUrl: string;
  effectiveHeightPx: number;
}

interface OverflowingScroller {
  scroller: HTMLElement;
  extraHeight: number;
}

function findOverflowingScroller(widget: HTMLElement): OverflowingScroller | null {
  for (const node of widget.querySelectorAll<HTMLElement>('*')) {
    const extraHeight = node.scrollHeight - node.clientHeight;
    if (extraHeight <= 1) continue;
    const { overflowY } = window.getComputedStyle(node);
    if (overflowY === 'auto' || overflowY === 'scroll') return { scroller: node, extraHeight };
  }
  return null;
}

// Every widget is captured from a clone on an off-screen stage: fencing the on-screen card would
// flash it white in dark mode for the length of the capture.
async function captureWidget(snapdom: Snapdom, widget: HTMLElement): Promise<WidgetCapture> {
  const rect = widget.getBoundingClientRect();
  const overflow = findOverflowingScroller(widget);
  const expandedHeight = rect.height + (overflow?.extraHeight ?? 0);

  const clone = widget.cloneNode(true) as HTMLElement;
  clone.style.height = `${expandedHeight}px`;
  if (overflow) {
    const descendants = [...widget.querySelectorAll<HTMLElement>('*')];
    const scrollerIndex = descendants.indexOf(overflow.scroller);
    const cloneScroller = clone.querySelectorAll<HTMLElement>('*')[scrollerIndex];
    if (cloneScroller) {
      cloneScroller.style.maxHeight = 'none';
      cloneScroller.style.overflowY = 'visible';
    }
  }

  const stage = document.createElement('div');
  stage.className = `${EXPORT_STAGE_CLASS} ${LIGHT_SURFACE_CLASS}`;
  stage.style.position = 'fixed';
  stage.style.left = '-100000px';
  stage.style.top = '0';
  stage.style.width = `${rect.width}px`;
  stage.setAttribute('aria-hidden', 'true');
  stage.append(clone);
  (widget.parentElement ?? document.body).append(stage);

  try {
    const canvas = await snapdom.toCanvas(clone, CAPTURE_OPTIONS);
    return { dataUrl: canvas.toDataURL('image/png'), effectiveHeightPx: expandedHeight };
  } finally {
    stage.remove();
  }
}

function truncateToWidth(doc: jsPDF, text: string, maxWidth: number): string {
  if (doc.getTextWidth(text) <= maxWidth) return text;
  let truncated = text;
  while (truncated.length > 0 && doc.getTextWidth(`${truncated.trimEnd()}…`) > maxWidth) {
    truncated = truncated.slice(0, -1);
  }
  return `${truncated.trimEnd()}…`;
}

function drawPageHeader(
  doc: jsPDF,
  branchName: string,
  periodLabel: string,
  generatedAtLabel: string
): void {
  const { margin, pageWidth, headerHeight } = PAGE_CONFIG;
  const rightX = pageWidth - margin;

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(10);
  doc.setTextColor(HEADER_TEXT_COLOR);
  const periodWidth = doc.getTextWidth(periodLabel);
  doc.text(periodLabel, rightX, margin + 5, { align: 'right' });

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(12);
  const nameMaxWidth = pageWidth - margin * 2 - periodWidth - 4;
  doc.text(truncateToWidth(doc, branchName, nameMaxWidth), margin, margin + 5);

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(8.5);
  doc.setTextColor(HEADER_SUBTEXT_COLOR);
  doc.text(`Gerado em ${generatedAtLabel}`, margin, margin + 10);

  doc.setDrawColor(HEADER_LINE_COLOR);
  doc.line(margin, margin + headerHeight - 3, rightX, margin + headerHeight - 3);
}

export function useDashboardExport(): {
  isExporting: Readonly<Ref<boolean>>;
  exportPdf: () => Promise<void>;
} {
  const isExporting = ref(false);
  const dashboardStore = useDashboardStore();
  const { currentBranch } = useBranchLogo();
  const toast = useCustomToast();
  let previousBlobUrl: string | null = null;

  async function exportPdf(): Promise<void> {
    if (isExporting.value) return;
    isExporting.value = true;
    try {
      if (!dashboardStore.data) throw new Error('Dashboard sem dados carregados');
      const container = document.querySelector<HTMLElement>('[data-testid="dashboard-widgets"]');
      if (!container) throw new Error('Grid do dashboard não encontrado');
      const widgets = [...container.querySelectorAll<HTMLElement>('[data-testid="widget-card"]')];
      if (widgets.length === 0) throw new Error('Nenhum widget para exportar');

      const branchName = currentBranch.value?.name ?? '';
      const periodLabel = formatPeriodLabel(dashboardStore.granularity, dashboardStore.period);
      const generatedAtLabel = buildGeneratedAtLabel(new Date());
      const fileName = buildExportFileName(branchName, dashboardStore.period);

      const [{ snapdom }, { jsPDF }] = await Promise.all([
        import('@zumer/snapdom'),
        import('jspdf'),
      ]);

      await new Promise((resolve) => setTimeout(resolve, CAPTURE_SETTLE_MS));

      const containerRect = container.getBoundingClientRect();
      const rects: WidgetRect[] = widgets.map((widget) => {
        const rect = widget.getBoundingClientRect();
        return {
          x: rect.x - containerRect.x,
          y: rect.y - containerRect.y,
          width: rect.width,
          height: rect.height,
        };
      });

      const captures: WidgetCapture[] = [];
      for (const widget of widgets) {
        captures.push(await captureWidget(snapdom, widget));
      }

      const pages = buildPdfLayout(
        rects,
        captures.map((capture) => capture.effectiveHeightPx),
        containerRect.width,
        PAGE_CONFIG
      );

      const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4', compress: true });
      pages.forEach((placements, pageIndex) => {
        if (pageIndex > 0) doc.addPage();
        drawPageHeader(doc, branchName, periodLabel, generatedAtLabel);
        for (const placement of placements) {
          doc.addImage(
            captures[placement.index].dataUrl,
            'PNG',
            placement.x,
            placement.y,
            placement.width,
            placement.height
          );
        }
      });

      doc.setProperties({ title: fileName });
      if (previousBlobUrl) URL.revokeObjectURL(previousBlobUrl);
      const blobUrl = String(doc.output('bloburl'));
      previousBlobUrl = blobUrl;
      const previewWindow = window.open(blobUrl, '_blank');
      if (!previewWindow) throw new Error('Não foi possível abrir a aba de visualização');
      toast.showSuccessToast({ title: 'Dashboard exportado com sucesso', type: 'success' });
    } catch {
      toast.showErrorToast({
        title: 'Erro ao exportar o dashboard, tente novamente.',
        type: 'error',
      });
    } finally {
      isExporting.value = false;
    }
  }

  return { isExporting: readonly(isExporting), exportPdf };
}
